import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { isSuperAdminRole } from '../../lib/roles';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command';
import { toast } from 'sonner';
import {
  Search,
  Upload,
  Download,
  UserPlus,
  Eye,
  Edit,
  Users,
  Settings,
  ChevronDown,
  User,
  Crown,
  Award,
  Star,
  Trophy,
  KeyRound,
  Copy,
  CheckCircle2,
  Network,
  Database,
  Check,
  ChevronsUpDown,
} from 'lucide-react';
import { History as HistoryIcon, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// Rank configuration with colors matching reference design
const RANK_CONFIG = {
  agent: { label: 'NEG', color: 'bg-gray-100 text-gray-600', badgeColor: 'bg-gray-500', icon: User },
  neg: { label: 'NEG', color: 'bg-gray-100 text-gray-600', badgeColor: 'bg-gray-500', icon: User },
  rm: { label: 'RM', color: 'bg-purple-100 text-purple-600', badgeColor: 'bg-purple-500', icon: Award },
  avp: { label: 'AVP', color: 'bg-blue-100 text-blue-600', badgeColor: 'bg-blue-500', icon: Star },
  svp: { label: 'SVP', color: 'bg-green-100 text-green-600', badgeColor: 'bg-green-500', icon: Crown },
  evp: { label: 'EVP', color: 'bg-pink-100 text-pink-600', badgeColor: 'bg-pink-500', icon: Trophy },
};

const STATUS_CONFIG = {
  active: { label: 'active', color: 'bg-green-50 text-green-600 border border-green-200' },
  inactive: { label: 'inactive', color: 'bg-gray-50 text-gray-500 border border-gray-200' },
  pending: { label: 'pending', color: 'bg-yellow-50 text-yellow-600 border border-yellow-200' },
};

// Avatar colors for consistent display
const AVATAR_COLORS = [
  '#F472B6', '#A78BFA', '#60A5FA', '#34D399', '#FBBF24', '#FB7185', '#818CF8', '#2DD4BF'
];

const getAvatarColor = (name) => {
  const index = (name?.charCodeAt(0) || 0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

const AgentManagement = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = isSuperAdminRole(currentUser?.role) || currentUser?.role === 'admin';
  const [agents, setAgents] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [rankFilter, setRankFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [showAddAgent, setShowAddAgent] = useState(false);
  const [showEditAgent, setShowEditAgent] = useState(false);
  const [showChangeHierarchy, setShowChangeHierarchy] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showBulkReset, setShowBulkReset] = useState(false);
  const [showUplineBackfill, setShowUplineBackfill] = useState(false);
  const [showLegacySeed, setShowLegacySeed] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  useEffect(() => {
    loadData();
  }, [search, rankFilter, statusFilter]);

  const loadData = async () => {
    setLoading(true);  // Show spinner on every filter / search re-trigger,
                       // not just initial mount.
    try {
      // Server-side filter to agent ranks only + bump limit to cover all agent ranks
      const res = await api.get('/users', {
        params: { role: 'agent,neg,rm,avp,svp,evp', limit: 3000 },
      });
      const users = res.data;
      setAllUsers(users);
      
      // Filter based on search and filters
      let filtered = users.filter(u => 
        ['agent', 'neg', 'rm', 'avp', 'svp', 'evp'].includes(u.role)
      );
      
      if (search) {
        const searchLower = search.toLowerCase();
        filtered = filtered.filter(u => 
          u.full_name?.toLowerCase().includes(searchLower) ||
          u.agent_code?.toLowerCase().includes(searchLower) ||
          u.user_code?.toLowerCase().includes(searchLower) ||
          u.email?.toLowerCase().includes(searchLower)
        );
      }
      
      if (rankFilter && rankFilter !== 'all') {
        filtered = filtered.filter(u => {
          const rank = u.agent_rank || u.role;
          // "agent" and "neg" are the same rank
          if (rankFilter === 'neg') return rank === 'neg' || rank === 'agent';
          return rank === rankFilter;
        });
      }
      
      if (statusFilter && statusFilter !== 'all') {
        filtered = filtered.filter(u => u.status === statusFilter);
      }
      
      setAgents(filtered);
    } catch (error) {
      console.error('Error loading agents:', error);
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedAgents(agents.map(a => a.id));
    } else {
      setSelectedAgents([]);
    }
  };

  const handleSelectAgent = (agentId, checked) => {
    if (checked) {
      setSelectedAgents([...selectedAgents, agentId]);
    } else {
      setSelectedAgents(selectedAgents.filter(id => id !== agentId));
    }
  };

  const handleViewAgent = (agent) => {
    navigate(`/admin/agents/${agent.id}`);
  };

  const handleEditAgent = (agent) => {
    setSelectedAgent(agent);
    setShowEditAgent(true);
  };

  const handleChangeHierarchy = (agent) => {
    setSelectedAgent(agent);
    setShowChangeHierarchy(true);
  };

  // Look up an agent's upline from the cached allUsers list.
  const getUpline = (agent) => {
    if (!agent?.upline_id) return null;
    return allUsers.find(u => u.id === agent.upline_id) || null;
  };

  // V4.18.29 — Look up an agent's introducer from the cached allUsers list.
  // Introducers are always agents (any rank), so allUsers covers them.
  const getIntroducer = (agent) => {
    if (!agent?.introducer_id) return null;
    return allUsers.find(u => u.id === agent.introducer_id) || null;
  };

  const handleExport = () => {
    // Escape CSV cells properly — full_name and email may contain commas/quotes.
    const csvEscape = (val) => {
      if (val === null || val === undefined) return '';
      const s = String(val);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rankLabel = (rank) => (rank || '').toString().toUpperCase();
    const headers = [
      'Agent ID', 'Agent Code', 'Rank', 'Full Name', 'Email', 'Phone',
      'Status', 'Upline ID', 'Upline Code', 'Upline Rank', 'Upline Name',
      'Total Commission', 'Sales Count', 'Created At',
    ];
    const rows = agents.map((a) => {
      const up = getUpline(a);
      return [
        a.id,
        a.agent_code,
        rankLabel(a.agent_rank || a.role),
        a.full_name,
        a.email,
        a.phone || a.contact_number,
        a.status || 'active',
        up?.id || '',
        up?.agent_code || '',
        rankLabel(up?.agent_rank || up?.role),
        up?.full_name || '',
        a.total_commission_earned || a.total_earned || 0,
        a.total_sales_count || 0,
        a.created_at || '',
      ].map(csvEscape).join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agents_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success(`Exported ${agents.length} agents`);
  };

  const getRankBadge = (rank) => {
    const config = RANK_CONFIG[rank] || RANK_CONFIG.agent;
    return (
      <Badge className={`${config.color} font-medium px-3 py-1 rounded-full text-xs`}>
        {config.label}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.active;
    return (
      <Badge className={`${config.color} font-medium px-3 py-1 rounded-full text-xs`}>
        {config.label}
      </Badge>
    );
  };

  // Count downlines for an agent
  const getDownlineCount = (agentId) => {
    return allUsers.filter(u => u.upline_id === agentId).length;
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="agent-management-page">
      {/* Header */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Agent Management</h1>
          <p className="text-xs sm:text-sm text-gray-500">Manage agents, uplines, and hierarchy</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Button variant="outline" size="sm" className="sm:size-default" onClick={() => toast.info('Import feature coming soon')}>
            <Upload className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Import CSV</span>
          </Button>
          <Button variant="outline" size="sm" className="sm:size-default" onClick={handleExport}>
            <Download className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Export</span>
          </Button>
          {isSuperAdmin && selectedAgents.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="sm:size-default border-amber-400 text-amber-700 hover:bg-amber-50"
              onClick={() => setShowBulkReset(true)}
              data-testid="bulk-reset-password-btn"
            >
              <KeyRound className="w-4 h-4 sm:mr-2" />
              <span className="hidden xs:inline sm:inline">Reset Passwords ({selectedAgents.length})</span>
              <span className="xs:hidden sm:hidden">({selectedAgents.length})</span>
            </Button>
          )}
          {isSuperAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="sm:size-default border-indigo-400 text-indigo-700 hover:bg-indigo-50"
              onClick={() => setShowUplineBackfill(true)}
              data-testid="legacy-upline-backfill-btn"
            >
              <Network className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Legacy Upline Backfill</span>
            </Button>
          )}
          {isSuperAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="sm:size-default border-rose-400 text-rose-700 hover:bg-rose-50"
              onClick={() => setShowLegacySeed(true)}
              data-testid="legacy-seed-import-btn"
            >
              <Database className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Legacy Data Import</span>
            </Button>
          )}
          <Button 
            size="sm"
            className="sm:size-default bg-purple-600 hover:bg-purple-700"
            onClick={() => setShowAddAgent(true)}
          >
            <UserPlus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Add Agent</span>
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by name, code, or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
        <Select value={rankFilter} onValueChange={setRankFilter}>
          <SelectTrigger className="w-32 sm:w-36">
            <SelectValue placeholder="All Ranks" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Ranks</SelectItem>
            <SelectItem value="neg">NEG</SelectItem>
            <SelectItem value="rm">RM</SelectItem>
            <SelectItem value="avp">AVP</SelectItem>
            <SelectItem value="svp">SVP</SelectItem>
            <SelectItem value="evp">EVP</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        </div>
      </div>

      {/* Agents Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-12">
                <Checkbox
                  checked={selectedAgents.length === agents.length && agents.length > 0}
                  onCheckedChange={handleSelectAll}
                />
              </TableHead>
              <TableHead className="text-gray-600 font-semibold">AGENT</TableHead>
              <TableHead className="text-gray-600 font-semibold">AGENT CODE</TableHead>
              <TableHead className="text-gray-600 font-semibold">EMAIL</TableHead>
              <TableHead className="text-gray-600 font-semibold">CONTACT</TableHead>
              <TableHead className="text-gray-600 font-semibold">RANK</TableHead>
              <TableHead className="text-gray-600 font-semibold">UPLINE</TableHead>
              <TableHead className="text-gray-600 font-semibold" data-testid="col-introducer">INTRODUCED BY</TableHead>
              <TableHead className="text-gray-600 font-semibold">STATUS</TableHead>
              <TableHead className="text-gray-600 font-semibold">COMMISSION</TableHead>
              <TableHead className="text-gray-600 font-semibold">ACTIONS</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-12">
                  <div className="animate-spin w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full mx-auto" />
                </TableCell>
              </TableRow>
            ) : agents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-12 text-gray-500">
                  No agents found
                </TableCell>
              </TableRow>
            ) : (
              agents.map((agent) => (
                <TableRow key={agent.id} className="hover:bg-gray-50" data-testid={`agent-row-${agent.id}`}>
                  <TableCell>
                    <Checkbox
                      checked={selectedAgents.includes(agent.id)}
                      onCheckedChange={(checked) => handleSelectAgent(agent.id, checked)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                        style={{ backgroundColor: getAvatarColor(agent.full_name) }}
                      >
                        {agent.full_name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{agent.full_name}</p>
                        {agent.user_code && (
                          <span className="font-mono text-xs text-[#4E50FF] bg-[#4E50FF]/5 px-1.5 py-0.5 rounded">
                            {agent.user_code}
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-sm text-gray-700" data-testid={`agent-code-${agent.id}`}>
                      {agent.agent_code || <span className="text-gray-400 italic">—</span>}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-700">{agent.email || <span className="text-gray-400 italic">—</span>}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-700">{agent.phone || agent.contact_number || <span className="text-gray-400 italic">—</span>}</span>
                  </TableCell>
                  <TableCell>
                    {getRankBadge(agent.agent_rank || agent.role)}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const upline = getUpline(agent);
                      if (!upline) return <span className="text-gray-400 italic text-xs">—</span>;
                      const uplineRank = upline.agent_rank || upline.role;
                      const cleanName = (upline.full_name || '').replace(/^\([^)]+\)\s*/, '').replace(/\s*\([^)]+\)\s*$/, '').replace(/\s*\[[^\]]+\]\s*$/, '').trim() || upline.full_name;
                      return (
                        <div className="flex flex-col" data-testid={`agent-upline-${agent.id}`}>
                          <div className="flex items-center gap-1.5">
                            {getRankBadge(uplineRank)}
                            <span className="font-mono text-xs text-gray-600">{upline.agent_code || '—'}</span>
                          </div>
                          <span className="text-xs text-gray-700 mt-0.5 truncate max-w-[180px]" title={upline.full_name}>{cleanName}</span>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell data-testid={`agent-introducer-cell-${agent.id}`}>
                    {(() => {
                      const intro = getIntroducer(agent);
                      if (!intro) return <span className="text-gray-400 italic text-xs">—</span>;
                      const introRank = intro.agent_rank || intro.role;
                      const cleanName = (intro.full_name || '').replace(/^\([^)]+\)\s*/, '').replace(/\s*\([^)]+\)\s*$/, '').replace(/\s*\[[^\]]+\]\s*$/, '').trim() || intro.full_name;
                      return (
                        <div className="flex flex-col" data-testid={`agent-introducer-${agent.id}`}>
                          <div className="flex items-center gap-1.5">
                            {getRankBadge(introRank)}
                            <span className="font-mono text-xs text-gray-600">{intro.agent_code || intro.user_code || '—'}</span>
                          </div>
                          <span className="text-xs text-gray-700 mt-0.5 truncate max-w-[180px]" title={intro.full_name}>{cleanName}</span>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(agent.status || 'active')}
                  </TableCell>
                  <TableCell>
                    <div>
                      <span className="font-semibold text-gray-900">
                        RM {(agent.total_commission_earned || agent.total_earned || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      {(agent.total_sales_count || 0) > 0 && (
                        <p className="text-xs text-gray-500">{agent.total_sales_count} sales · 2024–2026</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-400 hover:text-purple-600 hover:bg-purple-50"
                        onClick={() => handleViewAgent(agent)}
                        title="View Details"
                        data-testid={`view-agent-${agent.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-400 hover:text-purple-600 hover:bg-purple-50"
                        onClick={() => handleEditAgent(agent)}
                        title="Edit Agent"
                        data-testid={`edit-agent-${agent.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-400 hover:text-orange-600 hover:bg-orange-50"
                        onClick={() => handleChangeHierarchy(agent)}
                        title="Change Upline"
                        data-testid={`hierarchy-agent-${agent.id}`}
                      >
                        <Users className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50"
                        onClick={() => { setSelectedAgent(agent); setShowHistory(true); }}
                        title="View History"
                        data-testid={`history-agent-${agent.id}`}
                      >
                        <HistoryIcon className="w-4 h-4" />
                      </Button>
                      {isSuperAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-400 hover:text-amber-600 hover:bg-amber-50"
                          onClick={() => { setSelectedAgent(agent); setShowResetPassword(true); }}
                          title="Reset Password"
                          data-testid={`reset-password-${agent.id}`}
                        >
                          <KeyRound className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Edit Agent Sheet */}
      <EditAgentSheet
        open={showEditAgent}
        onOpenChange={setShowEditAgent}
        agent={selectedAgent}
        allUsers={allUsers}
        isSuperAdmin={isSuperAdmin}
        onSave={() => {
          loadData();
          setShowEditAgent(false);
        }}
      />

      {/* Reset Password Dialog (Super Admin only) */}
      <ResetPasswordDialog
        open={showResetPassword}
        onOpenChange={setShowResetPassword}
        agent={selectedAgent}
      />

      {/* Agent History Side-Sheet (Commission Engine V2) */}
      <AgentHistorySheet
        open={showHistory}
        onOpenChange={setShowHistory}
        agent={selectedAgent}
        allUsers={allUsers}
      />

      {/* Bulk Reset Password Dialog (Super Admin only) */}
      <BulkResetPasswordDialog
        open={showBulkReset}
        onOpenChange={setShowBulkReset}
        selectedAgents={agents.filter(a => selectedAgents.includes(a.id))}
        onComplete={() => setSelectedAgents([])}
      />

      {/* Legacy Upline Backfill Dialog (Super Admin only) */}
      <LegacyUplineBackfillDialog
        open={showUplineBackfill}
        onOpenChange={setShowUplineBackfill}
        onComplete={loadData}
      />

      {/* Legacy Data Import Dialog (Super Admin only) */}
      <LegacySeedImportDialog
        open={showLegacySeed}
        onOpenChange={setShowLegacySeed}
        onComplete={loadData}
      />

      {/* Change Hierarchy Dialog */}
      <ChangeHierarchyDialog
        open={showChangeHierarchy}
        onOpenChange={setShowChangeHierarchy}
        agent={selectedAgent}
        allUsers={allUsers}
        onSave={() => {
          loadData();
          setShowChangeHierarchy(false);
        }}
      />

      {/* Add Agent Dialog */}
      <AddAgentDialog
        open={showAddAgent}
        onOpenChange={setShowAddAgent}
        allUsers={allUsers}
        onSave={() => {
          loadData();
          setShowAddAgent(false);
        }}
      />
    </div>
  );
};

// Edit Agent Sheet Component
const EditAgentSheet = ({ open, onOpenChange, agent, allUsers, isSuperAdmin, onSave }) => {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [introducerOpen, setIntroducerOpen] = useState(false);

  useEffect(() => {
    if (agent) {
      setFormData({
        full_name: agent.full_name || '',
        nickname: agent.nickname || '',
        email: agent.email || '',
        phone: agent.phone || '',
        contact_number: agent.contact_number || '',
        ic_passport: agent.ic_passport || '',
        address: agent.address || '',
        bank_name: agent.bank_name || '',
        bank_account: agent.bank_account || '',
        mobile_wallet: agent.mobile_wallet || '',
        agent_rank: (agent.agent_rank === 'agent' ? 'neg' : agent.agent_rank) || (agent.role === 'agent' ? 'neg' : agent.role) || 'neg',
        agent_code: agent.agent_code || '',
        status: agent.status || 'active',
        employment_status: agent.employment_status || (agent.full_time_agent ? 'full_time' : 'part_time'),
        effective_at: new Date().toISOString().slice(0, 10),
        change_reason: '',
        upline_id: agent.upline_id || '',
        // V4.18.29 — Introducer id (persisted separately via /admin/users/{id}/introducer)
        introducer_id: agent.introducer_id || '',
        referred_by_code: agent.referred_by_code || '',
        commission_percentage: agent.commission_percentage || 2.5,
        kyc_status: agent.kyc_status || 'pending',
        full_time_agent: agent.full_time_agent || false,
      });
    }
  }, [agent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Strip agent_code from payload unless it actually changed AND user is super admin
      const payload = { ...formData };
      if (!isSuperAdmin || payload.agent_code === agent.agent_code) {
        delete payload.agent_code;
      }
      // V4.18.29 — introducer_id has its own dedicated admin endpoint
      // (kept out of the generic /users PUT so an audit trail is written
      // and self-introduction is guarded). Peel it off here and sync
      // separately only if the value actually changed.
      const nextIntroducerId = payload.introducer_id || null;
      const prevIntroducerId = agent.introducer_id || null;
      delete payload.introducer_id;

      await api.put(`/users/${agent.id}`, payload);
      if (nextIntroducerId !== prevIntroducerId) {
        try {
          await api.put(`/admin/users/${agent.id}/introducer`, {
            introducer_code: nextIntroducerId || null,  // null → clear
          });
        } catch (introErr) {
          // Surface but don't block — the main /users update already succeeded.
          toast.error(introErr?.response?.data?.detail?.message || introErr?.response?.data?.detail || 'Failed to update introducer');
        }
      }
      toast.success('Agent updated successfully');
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update agent');
    } finally {
      setSaving(false);
    }
  };

  if (!agent) return null;

  // Agents have role="agent" in this codebase — the actual hierarchy rank
  // lives in `agent_rank`. Prefer agent_rank, fall back to role for legacy
  // seed rows. (This was the bug producing an empty Upline dropdown.)
  const uplineOptions = allUsers.filter(u => {
    if (u.id === agent.id) return false;
    const rank = (u.agent_rank || u.role || '').toLowerCase();
    return ['neg', 'rm', 'avp', 'svp', 'evp'].includes(rank);
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-xl">
            <div>Edit Agent</div>
            <div className="text-sm font-normal text-gray-500">
              {agent.agent_code} - {agent.full_name}
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 font-semibold text-gray-900">
              <User className="w-4 h-4 text-purple-600" />
              Personal Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Full Name *</label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Nickname</label>
                <Input
                  value={formData.nickname}
                  onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                  placeholder="Enter nickname"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Email *</label>
                <Input
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Phone *</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Contact Number</label>
                <Input
                  value={formData.contact_number}
                  onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">IC/Passport Number</label>
                <Input
                  value={formData.ic_passport}
                  onChange={(e) => setFormData({ ...formData, ic_passport: e.target.value })}
                  placeholder="Enter IC or passport number"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Address</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Enter full address"
              />
            </div>
          </div>

          {/* Banking Information */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 font-semibold text-gray-900">
              <svg className="w-4 h-4 text-purple-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M4 10h16v10H4V10zm0-4h16l-8-4-8 4zm2 6h2v6H6v-6zm4 0h2v6h-2v-6zm4 0h2v6h-2v-6z"/>
              </svg>
              Banking Information
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Bank Name</label>
                <Input
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  placeholder="e.g., Maybank"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Bank Account</label>
                <Input
                  value={formData.bank_account}
                  onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                  placeholder="Account number"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Mobile Wallet</label>
                <Input
                  value={formData.mobile_wallet}
                  onChange={(e) => setFormData({ ...formData, mobile_wallet: e.target.value })}
                  placeholder="e.g., Touch 'n Go"
                />
              </div>
            </div>
          </div>

          {/* Hierarchy & Commission */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 font-semibold text-gray-900">
              <Users className="w-4 h-4 text-purple-600" />
              Hierarchy & Commission
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Rank</label>
                <Select 
                  value={formData.agent_rank} 
                  onValueChange={(v) => setFormData({ ...formData, agent_rank: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="neg">NEG</SelectItem>
                    <SelectItem value="rm">RM</SelectItem>
                    <SelectItem value="avp">AVP</SelectItem>
                    <SelectItem value="svp">SVP</SelectItem>
                    <SelectItem value="evp">EVP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Status</label>
                <Select 
                  value={formData.status} 
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Employment Status</label>
                <Select 
                  value={formData.employment_status} 
                  onValueChange={(v) => setFormData({ ...formData, employment_status: v, full_time_agent: v === 'full_time' })}
                >
                  <SelectTrigger data-testid="employment-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">Full-time</SelectItem>
                    <SelectItem value="part_time">Part-time</SelectItem>
                    <SelectItem value="resigned">Resigned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Upline</label>
                <Select 
                  value={formData.upline_id || 'none'} 
                  onValueChange={(v) => setFormData({ ...formData, upline_id: v === 'none' ? null : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select upline" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Upline (Top Level)</SelectItem>
                    {uplineOptions.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.agent_code} - {u.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* V4.18.29 — Introducer (searchable) */}
              <div>
                <label className="text-sm font-medium text-gray-700">Introduced By</label>
                <Popover open={introducerOpen} onOpenChange={setIntroducerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between font-normal"
                      data-testid="introducer-combobox-trigger"
                    >
                      {(() => {
                        if (!formData.introducer_id) return <span className="text-gray-400">Select introducer (optional)</span>;
                        const intro = allUsers.find(u => u.id === formData.introducer_id);
                        if (!intro) return <span className="text-gray-500 italic">Unknown — {formData.introducer_id.slice(0, 8)}</span>;
                        return <span className="truncate">{intro.agent_code || intro.user_code || '—'} · {intro.full_name}</span>;
                      })()}
                      <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command
                      filter={(value, search) => {
                        // `value` is the id — build the searchable string from the option
                        const opt = allUsers.find(u => u.id === value);
                        if (!opt) return 0;
                        const haystack = `${opt.agent_code || ''} ${opt.user_code || ''} ${opt.full_name || ''} ${opt.email || ''}`.toLowerCase();
                        return haystack.includes(search.toLowerCase()) ? 1 : 0;
                      }}
                    >
                      <CommandInput placeholder="Search by name / code / email…" data-testid="introducer-search-input" />
                      <CommandList>
                        <CommandEmpty>No agent found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value=""
                            onSelect={() => {
                              setFormData({ ...formData, introducer_id: '' });
                              setIntroducerOpen(false);
                            }}
                            data-testid="introducer-option-none"
                          >
                            <Check className={`mr-2 h-4 w-4 ${!formData.introducer_id ? 'opacity-100' : 'opacity-0'}`} />
                            <span className="text-gray-500 italic">No introducer</span>
                          </CommandItem>
                          {allUsers
                            .filter(u => u.id !== agent.id && ['agent', 'neg', 'rm', 'avp', 'svp', 'evp'].includes((u.agent_rank || u.role || '').toLowerCase()))
                            .map(u => (
                              <CommandItem
                                key={u.id}
                                value={u.id}
                                onSelect={(val) => {
                                  setFormData({ ...formData, introducer_id: val });
                                  setIntroducerOpen(false);
                                }}
                                data-testid={`introducer-option-${u.id}`}
                              >
                                <Check className={`mr-2 h-4 w-4 ${formData.introducer_id === u.id ? 'opacity-100' : 'opacity-0'}`} />
                                <div className="flex flex-col">
                                  <span className="font-mono text-xs text-gray-600">{u.agent_code || u.user_code || '—'} · {(u.agent_rank || u.role || '').toUpperCase()}</span>
                                  <span className="text-sm">{u.full_name}</span>
                                </div>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-[11px] text-gray-500 mt-1">
                  Only used for NEG-newcomer tracking. Does not change the active upline.
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Referred By Code</label>
                <Input
                  value={formData.referred_by_code}
                  onChange={(e) => setFormData({ ...formData, referred_by_code: e.target.value })}
                  placeholder="Enter referral code"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Commission %</label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.commission_percentage}
                  onChange={(e) => setFormData({ ...formData, commission_percentage: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">KYC Status</label>
                <Select 
                  value={formData.kyc_status} 
                  onValueChange={(v) => setFormData({ ...formData, kyc_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-3" data-testid="effective-dated-fields">
              <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/></svg>
                Effective-dated change (Rank / Upline / Employment)
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-amber-800">Effective From</label>
                  <Input
                    type="date"
                    value={formData.effective_at || ''}
                    onChange={(e) => setFormData({ ...formData, effective_at: e.target.value })}
                    data-testid="effective-from-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-amber-800">Reason</label>
                  <Input
                    value={formData.change_reason || ''}
                    onChange={(e) => setFormData({ ...formData, change_reason: e.target.value })}
                    placeholder="e.g. Q3 promotion"
                    data-testid="change-reason-input"
                  />
                </div>
              </div>
              <p className="text-[11px] text-amber-700">
                Rank, upline or employment changes are written to the immutable history log with this date &amp; reason.
              </p>
            </div>
          </div>

          {/* Agent Codes */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">Agent Codes</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <label className="text-xs text-gray-500 block mb-1">
                  Agent Code {isSuperAdmin ? <span className="text-amber-600 font-medium">(Super Admin editable)</span> : <span className="text-gray-400">(read-only)</span>}
                </label>
                {isSuperAdmin ? (
                  <Input
                    value={formData.agent_code || ''}
                    onChange={(e) => setFormData({ ...formData, agent_code: e.target.value.toUpperCase() })}
                    className="font-mono"
                    data-testid="agent-code-edit-input"
                  />
                ) : (
                  <span className="font-mono font-semibold text-purple-600 block py-2" data-testid="agent-code-readonly">{agent.agent_code}</span>
                )}
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Referral Code (read-only)</label>
                <span className="font-mono block py-2">{agent.referral_code || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Update Agent'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Change Hierarchy Dialog Component
const ChangeHierarchyDialog = ({ open, onOpenChange, agent, allUsers, onSave }) => {
  const [newRank, setNewRank] = useState('');
  const [newUplineId, setNewUplineId] = useState('');
  const [uplineSearch, setUplineSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (agent) {
      setNewRank((agent.agent_rank === 'agent' ? 'neg' : agent.agent_rank) || (agent.role === 'agent' ? 'neg' : agent.role) || 'neg');
      setNewUplineId(agent.upline_id || '');
    }
  }, [agent]);

  if (!agent) return null;

  const currentUpline = agent.upline_id ? allUsers.find(u => u.id === agent.upline_id) : null;
  const newUpline = newUplineId ? allUsers.find(u => u.id === newUplineId) : null;

  const uplineOptions = allUsers.filter(u => {
    if (u.id === agent.id) return false;
    // Agents in this codebase have role="agent" and rank stored in agent_rank.
    // Filter by agent_rank (with a fallback to role for legacy seed rows).
    const rank = (u.agent_rank || u.role || '').toLowerCase();
    if (!['neg', 'rm', 'avp', 'svp', 'evp'].includes(rank)) return false;
    if (uplineSearch) {
      const search = uplineSearch.toLowerCase();
      return u.full_name?.toLowerCase().includes(search) ||
             u.agent_code?.toLowerCase().includes(search) ||
             u.email?.toLowerCase().includes(search);
    }
    return true;
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      // Upline-only update. Rank promotion is handled exclusively on the Edit page.
      await api.put(`/users/${agent.id}`, {
        upline_id: newUplineId || null,
      });
      toast.success('Upline updated successfully');
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update upline');
    } finally {
      setSaving(false);
    }
  };

  const ranks = [
    { value: 'neg', label: 'NEG', icon: User },
    { value: 'rm', label: 'RM', icon: Award },
    { value: 'avp', label: 'AVP', icon: Star },
    { value: 'svp', label: 'SVP', icon: Crown },
    { value: 'evp', label: 'EVP', icon: Trophy },
  ];

  const getRankLabel = (rank) => {
    const r = ranks.find(r => r.value === rank);
    return r ? r.label : rank?.toUpperCase();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl">
            <div>Change Upline</div>
            <div className="text-sm font-normal text-gray-500">
              {agent.full_name} ({agent.agent_code})
            </div>
            <div className="text-xs font-normal text-gray-400 mt-1">
              Tip: to promote or demote this agent&apos;s <strong>rank</strong>, open the Edit page (Hierarchy &amp; Commission section).
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Status */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-700 mb-2">Current Status</h4>
            <div className="flex items-center gap-6 text-sm">
              <div>
                <span className="text-gray-500">Rank:</span>
                <span className="ml-2 font-semibold">{getRankLabel(agent.agent_rank || agent.role)}</span>
              </div>
              <div>
                <span className="text-gray-500">Upline:</span>
                <span className="ml-2 font-semibold">
                  {currentUpline ? `${currentUpline.agent_code} - ${currentUpline.full_name}` : 'None'}
                </span>
              </div>
            </div>
          </div>

          {/* Select New Upline */}
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Select New Upline</h4>
            <Input
              placeholder="Search by name, code, or email..."
              value={uplineSearch}
              onChange={(e) => setUplineSearch(e.target.value)}
              className="mb-3"
            />
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              <button
                onClick={() => setNewUplineId('')}
                className={`w-full flex items-center gap-3 p-3 hover:bg-gray-50 border-b ${
                  !newUplineId ? 'bg-purple-50' : ''
                }`}
                data-testid="upline-none"
              >
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-500" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">No Upline</p>
                  <p className="text-xs text-gray-500">Set as top-level agent</p>
                </div>
                {!newUplineId && (
                  <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
              {uplineOptions.slice(0, 15).map((u) => (
                <button
                  key={u.id}
                  onClick={() => setNewUplineId(u.id)}
                  className={`w-full flex items-center gap-3 p-3 hover:bg-gray-50 border-b last:border-b-0 ${
                    newUplineId === u.id ? 'bg-purple-50' : ''
                  }`}
                  data-testid={`upline-${u.id}`}
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                    style={{ backgroundColor: getAvatarColor(u.full_name) }}
                  >
                    {u.full_name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900">{u.full_name}</p>
                    <p className="text-xs text-gray-500">
                      {u.agent_code} • {(u.role || u.agent_rank)?.toUpperCase()} • {u.email}
                    </p>
                  </div>
                  {newUplineId === u.id && (
                    <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Preview Changes */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h4 className="flex items-center gap-2 font-medium text-blue-800 mb-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Preview Changes
            </h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Upline will change to <strong>{newUpline ? `${newUpline.agent_code} - ${newUpline.full_name}` : 'None (Top Level)'}</strong></li>
            </ul>
            <p className="text-xs text-blue-600 mt-2">All related commissions will be automatically recalculated.</p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Updating...' : 'Update Hierarchy'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Add Agent Dialog Component
const AddAgentDialog = ({ open, onOpenChange, allUsers, onSave }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    agent_code: '',
    agent_rank: 'neg',
    upline_id: '',
    introducer_id: '',
    password: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!formData.full_name || !formData.email || !formData.agent_code) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const res = await api.post('/auth/register', {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone || '',
        agent_code: formData.agent_code,
        role: 'agent',
        agent_rank: formData.agent_rank,
        password: formData.password || 'password123',
        upline_id: formData.upline_id || undefined,
      });
      // Set the introducer via the dedicated audited endpoint (keeps the
      // append-only introducer history the commission/TLC engine relies on).
      const newId = res?.data?.user?.id;
      if (newId && formData.introducer_id) {
        try {
          await api.put(`/admin/users/${newId}/introducer`, { introducer_code: formData.introducer_id });
        } catch (e) {
          toast.warning("Agent created, but setting the introducer failed — you can set it from the agent's edit page.");
        }
      }
      toast.success('Agent added successfully');
      onSave();
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        agent_code: '',
        agent_rank: 'neg',
        upline_id: '',
        introducer_id: '',
        password: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add agent');
    } finally {
      setSaving(false);
    }
  };

  const uplineOptions = allUsers.filter(u => 
    ['neg', 'rm', 'avp', 'svp', 'evp'].includes(u.role)
  );
  // Introducer (recruiter) can be any agent-tier user — independent of upline.
  const introducerOptions = uplineOptions;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Agent</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Full Name *</label>
            <Input
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              placeholder="Enter full name"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Email *</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="Enter email"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Phone</label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="Enter phone"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Agent Code *</label>
            <Input
              value={formData.agent_code}
              onChange={(e) => setFormData({ ...formData, agent_code: e.target.value.toUpperCase() })}
              placeholder="e.g., NEG006"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Rank</label>
            <Select 
              value={formData.agent_rank} 
              onValueChange={(v) => setFormData({ ...formData, agent_rank: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="neg">NEG</SelectItem>
                <SelectItem value="rm">RM</SelectItem>
                <SelectItem value="avp">AVP</SelectItem>
                <SelectItem value="svp">SVP</SelectItem>
                <SelectItem value="evp">EVP</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Upline</label>
            <Select 
              value={formData.upline_id || 'none'} 
              onValueChange={(v) => setFormData({ ...formData, upline_id: v === 'none' ? '' : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select upline" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Upline</SelectItem>
                {uplineOptions.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.agent_code} - {u.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Introduced By</label>
            <Select
              value={formData.introducer_id || 'none'}
              onValueChange={(v) => setFormData({ ...formData, introducer_id: v === 'none' ? '' : v })}
            >
              <SelectTrigger data-testid="add-agent-introducer-select">
                <SelectValue placeholder="Select introducer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Introducer</SelectItem>
                {introducerOptions.map(u => (
                  <SelectItem key={u.id} value={u.id} data-testid={`add-agent-introducer-option-${u.id}`}>
                    {u.agent_code} - {u.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-gray-400 mt-1">Who recruited this agent (drives recruitment/TLC credit). Independent of Upline.</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Initial Password</label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Leave empty for default"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Adding...' : 'Add Agent'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AgentManagement;

// ==================== AGENT HISTORY SIDE-SHEET (Commission Engine V2) ====================
const RANK_LABELS = { neg: 'NEG', agent: 'NEG', rm: 'RM', avp: 'AVP', svp: 'SVP', evp: 'EVP' };
const EMP_LABELS = { full_time: 'Full-time', part_time: 'Part-time', resigned: 'Resigned', active: 'Active', inactive: 'Inactive' };

const AgentHistorySheet = ({ open, onOpenChange, agent, allUsers }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && agent?.id) {
      setLoading(true);
      api.get(`/comm-engine/agents/${agent.id}/history`)
        .then((res) => setHistory(res.data))
        .catch(() => toast.error('Failed to load history'))
        .finally(() => setLoading(false));
    } else {
      setHistory(null);
    }
  }, [open, agent]);

  const nameFor = (id) => {
    if (!id) return '—';
    const u = allUsers.find((x) => x.id === id);
    return u ? `${u.agent_code || u.user_code || ''} · ${u.full_name}` : id.slice(0, 8);
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

  const Timeline = ({ rows, render, empty }) => (
    rows && rows.length > 0 ? (
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={r.id || i} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className="w-2.5 h-2.5 rounded-full bg-indigo-500 mt-1.5" />
              {i < rows.length - 1 && <div className="w-px flex-1 bg-gray-200" />}
            </div>
            <div className="pb-3 flex-1">
              {render(r)}
              <p className="text-[11px] text-gray-400 mt-0.5">
                Effective {fmt(r.effective_at)}{r.reason ? ` · ${r.reason}` : ''}
                {r.updated_by === 'system_backfill' ? ' · (backfill)' : ''}
              </p>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-sm text-gray-400 italic py-4">{empty}</p>
    )
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="agent-history-sheet">
        <SheetHeader>
          <SheetTitle className="text-xl">
            <div className="flex items-center gap-2"><Clock className="w-5 h-5 text-indigo-600" /> Agent History</div>
            {agent && (
              <div className="text-sm font-normal text-gray-500">{agent.agent_code} — {agent.full_name}</div>
            )}
          </SheetTitle>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin w-7 h-7 border-2 border-indigo-600 border-t-transparent rounded-full" />
          </div>
        ) : history ? (
          <div className="space-y-6 mt-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Award className="w-4 h-4 text-purple-600" /> Rank History</h3>
              <Timeline
                rows={history.rank_history}
                empty="No rank changes recorded."
                render={(r) => (
                  <p className="text-sm font-medium text-gray-800">
                    {r.previous_rank ? `${RANK_LABELS[r.previous_rank] || r.previous_rank} → ` : ''}
                    {RANK_LABELS[r.rank] || r.rank}
                  </p>
                )}
              />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Network className="w-4 h-4 text-blue-600" /> Upline History</h3>
              <Timeline
                rows={history.upline_history}
                empty="No upline changes recorded."
                render={(r) => (
                  <p className="text-sm font-medium text-gray-800">→ {nameFor(r.active_upline_id)}</p>
                )}
              />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><User className="w-4 h-4 text-amber-600" /> Employment History</h3>
              <Timeline
                rows={history.employment_history}
                empty="No employment changes recorded."
                render={(r) => (
                  <p className="text-sm font-medium text-gray-800">
                    {r.previous_status ? `${EMP_LABELS[r.previous_status] || r.previous_status} → ` : ''}
                    {EMP_LABELS[r.status] || r.status}
                  </p>
                )}
              />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-gray-500" /> Introduced By (immutable)</h3>
              {history.introducer ? (
                <div className="rounded-lg bg-gray-50 border p-3">
                  <p className="text-sm font-medium text-gray-800">{nameFor(history.introducer.introduced_by)}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">Locked {fmt(history.introducer.effective_at)}</p>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic py-2">No introducer on record.</p>
              )}
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};

// ==================== BULK RESET PASSWORD DIALOG (Super Admin only) ====================
const BulkResetPasswordDialog = ({ open, onOpenChange, selectedAgents, onComplete }) => {
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [copiedRowId, setCopiedRowId] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);

  useEffect(() => {
    if (open) {
      setResults(null);
      setSendEmail(true);
      setCopiedRowId(null);
      setCopiedAll(false);
    }
  }, [open]);

  const handleGenerate = async () => {
    if (!selectedAgents?.length) return;
    setLoading(true);
    try {
      const res = await api.post('/auth/admin-bulk-reset-password', {
        user_ids: selectedAgents.map(a => a.id),
        send_email: sendEmail,
      });
      setResults(res.data);
      const { success_count, failure_count, email_sent_count, total } = res.data;
      if (failure_count === 0) {
        toast.success(`Reset ${success_count}/${total} passwords${sendEmail ? ` · ${email_sent_count} emails sent` : ''}`);
      } else {
        toast.warning(`Reset ${success_count}/${total} (${failure_count} failed) — check the results table`);
      }
      onComplete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk reset failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyRow = async (row) => {
    try {
      await navigator.clipboard.writeText(`${row.email}\t${row.temp_password}`);
      setCopiedRowId(row.user_id);
      setTimeout(() => setCopiedRowId(null), 1500);
    } catch {
      toast.error('Could not copy — please select manually');
    }
  };

  const handleCopyAll = async () => {
    if (!results?.results?.length) return;
    const csv = [
      'Agent Code,Full Name,Email,Temp Password,Email Sent',
      ...results.results
        .filter(r => r.success)
        .map(r => [r.agent_code || '', r.full_name || '', r.email || '', r.temp_password || '', r.email_sent ? 'YES' : 'NO']
          .map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(csv);
      setCopiedAll(true);
      toast.success(`Copied ${results.success_count} rows as CSV`);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch {
      toast.error('Clipboard unavailable — use Download CSV instead');
    }
  };

  const handleDownloadCsv = () => {
    if (!results?.results?.length) return;
    const csv = [
      'Agent Code,Full Name,Email,Temp Password,Email Sent,Success,Error',
      ...results.results.map(r => [
        r.agent_code || '', r.full_name || '', r.email || '',
        r.temp_password || '', r.email_sent ? 'YES' : 'NO',
        r.success ? 'YES' : 'NO', r.error || '',
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk_password_reset_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click();
    window.URL.revokeObjectURL(url); document.body.removeChild(a);
    toast.success('CSV downloaded');
  };

  if (!selectedAgents?.length && !results) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !loading && onOpenChange(o)}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bulk-reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-amber-600" />
            {results ? 'Bulk Password Reset — Results' : `Reset Passwords for ${selectedAgents.length} Agents`}
          </DialogTitle>
        </DialogHeader>

        {!results ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              You're about to generate temporary passwords for <span className="font-semibold">{selectedAgents.length}</span> agents.
              Each will be required to change their password on next login.
            </div>

            <div className="border rounded-lg max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs uppercase text-gray-500 font-semibold">Agent</th>
                    <th className="px-3 py-2 text-left text-xs uppercase text-gray-500 font-semibold">Code</th>
                    <th className="px-3 py-2 text-left text-xs uppercase text-gray-500 font-semibold">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedAgents.map(a => (
                    <tr key={a.id} className="border-t">
                      <td className="px-3 py-2">{a.full_name}</td>
                      <td className="px-3 py-2 font-mono text-xs">{a.agent_code || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{a.email || <span className="text-red-500 italic">no email</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="bulk-send-email"
                checked={sendEmail}
                onCheckedChange={setSendEmail}
                data-testid="bulk-send-email-checkbox"
              />
              <label htmlFor="bulk-send-email" className="text-sm text-gray-700 leading-snug">
                Email each agent their temporary password (agents without an email on file will be skipped from the email send but still get a temp password)
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleGenerate}
                disabled={loading}
                data-testid="bulk-generate-temp-passwords-btn"
              >
                {loading ? 'Generating…' : `Generate ${selectedAgents.length} Temp Passwords`}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-xs uppercase text-emerald-700">Success</p>
                <p className="text-2xl font-bold text-emerald-700">{results.success_count}</p>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-xs uppercase text-red-700">Failed</p>
                <p className="text-2xl font-bold text-red-700">{results.failure_count}</p>
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-xs uppercase text-blue-700">Emails Sent</p>
                <p className="text-2xl font-bold text-blue-700">{results.email_sent_count}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={handleCopyAll} data-testid="bulk-copy-all-btn">
                {copiedAll ? <CheckCircle2 className="w-4 h-4 mr-1 text-emerald-600" /> : <Copy className="w-4 h-4 mr-1" />}
                Copy All as CSV
              </Button>
              <Button size="sm" variant="outline" onClick={handleDownloadCsv} data-testid="bulk-download-csv-btn">
                <Download className="w-4 h-4 mr-1" /> Download CSV
              </Button>
              <p className="text-xs text-amber-700 ml-auto">⚠️ Save this list — temp passwords cannot be retrieved later.</p>
            </div>

            <div className="border rounded-lg max-h-80 overflow-y-auto">
              <table className="w-full text-sm" data-testid="bulk-results-table">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs uppercase text-gray-500 font-semibold">Agent</th>
                    <th className="px-3 py-2 text-left text-xs uppercase text-gray-500 font-semibold">Email</th>
                    <th className="px-3 py-2 text-left text-xs uppercase text-gray-500 font-semibold">Temp Password</th>
                    <th className="px-3 py-2 text-left text-xs uppercase text-gray-500 font-semibold">Status</th>
                    <th className="px-3 py-2 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {results.results.map(r => (
                    <tr key={r.user_id} className={`border-t ${!r.success ? 'bg-red-50' : ''}`} data-testid={`bulk-result-row-${r.user_id}`}>
                      <td className="px-3 py-2">
                        <p className="font-medium">{r.full_name || <span className="text-gray-400 italic">unknown</span>}</p>
                        {r.agent_code && <p className="text-xs font-mono text-gray-500">{r.agent_code}</p>}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">{r.email || '—'}</td>
                      <td className="px-3 py-2">
                        {r.temp_password ? (
                          <code className="font-mono text-xs bg-amber-50 border border-amber-200 px-2 py-1 rounded select-all">
                            {r.temp_password}
                          </code>
                        ) : <span className="text-gray-400 italic text-xs">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        {r.success ? (
                          r.email_sent ? (
                            <Badge className="bg-blue-100 text-blue-700 border border-blue-200">Emailed</Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">Generated</Badge>
                          )
                        ) : (
                          <Badge className="bg-red-100 text-red-700 border border-red-200" title={r.error}>Failed</Badge>
                        )}
                        {r.email_error && <p className="text-[10px] text-red-600 mt-1 truncate max-w-[180px]" title={r.email_error}>{r.email_error}</p>}
                      </td>
                      <td className="px-3 py-2">
                        {r.temp_password && (
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleCopyRow(r)} title="Copy email + password">
                            {copiedRowId === r.user_id ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button onClick={() => onOpenChange(false)} className="bg-gray-700 hover:bg-gray-800 text-white">Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ResetPasswordDialog = ({ open, onOpenChange, agent }) => {
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [copied, setCopied] = useState(false);

  // Reset state whenever dialog opens or target agent changes
  useEffect(() => {
    if (open) {
      setTempPassword('');
      setEmailSent(false);
      setCopied(false);
      setSendEmail(true);
    }
  }, [open, agent?.id]);

  const handleGenerate = async () => {
    if (!agent?.id) return;
    setLoading(true);
    try {
      const res = await api.post('/auth/admin-reset-password', {
        user_id: agent.id,
        send_email: sendEmail,
      });
      setTempPassword(res.data.temp_password);
      setEmailSent(!!res.data.email_sent);
      if (sendEmail && res.data.email_sent) {
        toast.success(`Temp password emailed to ${agent.email}`);
      } else if (sendEmail && !res.data.email_sent) {
        toast.warning('Password generated, but email failed to send. Copy it manually.');
      } else {
        toast.success('Temporary password generated');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!tempPassword) return;
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      toast.success('Password copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy automatically — please select and copy manually');
    }
  };

  if (!agent) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-amber-600" />
            Reset Password
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
            <div><span className="text-gray-500">Agent:</span> <span className="font-semibold text-gray-900 ml-1">{agent.full_name}</span></div>
            <div><span className="text-gray-500">Code:</span> <span className="font-mono ml-1">{agent.agent_code || '—'}</span></div>
            <div><span className="text-gray-500">Email:</span> <span className="ml-1">{agent.email || '—'}</span></div>
          </div>

          {!tempPassword ? (
            <>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                A new temporary password will be generated. The agent will be required to change it on their next login.
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="send-email-checkbox"
                  checked={sendEmail}
                  onCheckedChange={setSendEmail}
                  disabled={!agent.email}
                  data-testid="send-email-checkbox"
                />
                <label htmlFor="send-email-checkbox" className="text-sm text-gray-700 leading-snug">
                  Email the temporary password to <span className="font-medium">{agent.email || 'the agent'}</span>
                  {!agent.email && <span className="block text-xs text-red-600">No email on file — copy required</span>}
                </label>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 p-4">
                <p className="text-xs uppercase tracking-wider text-amber-700 mb-1">Temporary Password</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-lg font-bold text-gray-900 bg-white px-3 py-2 rounded select-all" data-testid="temp-password-display">
                    {tempPassword}
                  </code>
                  <Button size="icon" variant="outline" onClick={handleCopy} title="Copy" data-testid="copy-temp-password-btn">
                    {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-amber-700 mt-2">
                  {emailSent ? `Emailed to ${agent.email}.` : 'Email not sent — copy this password and share it securely.'} The agent must change it on next login.
                </p>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              {tempPassword ? 'Done' : 'Cancel'}
            </Button>
            {!tempPassword && (
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleGenerate}
                disabled={loading}
                data-testid="generate-temp-password-btn"
              >
                {loading ? 'Generating…' : 'Generate Temp Password'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ==================== LEGACY UPLINE BACKFILL DIALOG (Super Admin only) ====================
const LegacyUplineBackfillDialog = ({ open, onOpenChange, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [dryRun, setDryRun] = useState(null);
  const [applied, setApplied] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/legacy-upline-mapping/status');
      setStatus(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setDryRun(null);
      setApplied(null);
      setConfirmText('');
      loadStatus();
    }
  }, [open]);

  const runDryRun = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/admin/backfill-legacy-uplines?mode=dry_run');
      setDryRun(data);
      toast.success(`Dry run complete: ${data.summary.total_actions} actions pending`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dry run failed');
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    if (confirmText !== 'APPLY') {
      toast.error('Type APPLY to confirm');
      return;
    }
    setLoading(true);
    try {
      const confirm = status?.confirm_token_for_apply;
      const { data } = await api.post(`/admin/backfill-legacy-uplines?mode=apply&confirm=${confirm}`);
      setApplied(data);
      toast.success(`Applied: ${data.summary.updated} users updated`);
      onComplete?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally {
      setLoading(false);
    }
  };

  const s = dryRun?.summary || applied?.summary;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="upline-backfill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Network className="w-5 h-5 text-indigo-600" />
            Legacy Upline Backfill
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-indigo-50 border border-indigo-200 rounded p-3 text-sm text-indigo-900">
            Rebuilds <code>users.upline_id</code> from the legacy SQL dump
            (<code>moz70_changeupline_uplinedetails</code>) using username/nickname/typo/downline fallbacks.
            Safe to run — self-references and dangling targets are skipped; every change is
            tagged with <code>upline_backfilled_at</code>.
          </div>

          {status && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-gray-500">Mapping entries</div>
                <div className="text-xl font-semibold" data-testid="status-mapping-entries">{status.mapping_entries}</div>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <div className="text-gray-500">Agents with upline</div>
                <div className="text-xl font-semibold">
                  {status.db_stats.with_upline} / {status.db_stats.total_agents}
                </div>
              </div>
              <div className="p-3 bg-gray-50 rounded col-span-2">
                <div className="text-gray-500">Mapping generated</div>
                <div className="font-mono text-xs">{status.mapping_generated_at}</div>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={runDryRun}
              disabled={loading}
              data-testid="dry-run-backfill-btn"
            >
              {loading ? 'Running…' : 'Run Dry Run'}
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open('/api/uploads/UPLINE_BACKFILL_REPORT.csv', '_blank')}
              data-testid="download-upline-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Preview CSV
            </Button>
          </div>

          {s && (
            <div className="border rounded p-3 space-y-1 text-sm">
              <div className="font-semibold text-gray-900 mb-2">Summary ({s.mode})</div>
              <div>Scanned: <b>{s.scanned}</b></div>
              <div>Already correct: <b className="text-green-700">{s.already_correct}</b></div>
              <div>Will set new (null → value): <b className="text-blue-700">{s.will_set_new}</b></div>
              <div>Will change (overwrite wrong): <b className="text-amber-700">{s.will_change}</b></div>
              <div>Total actions: <b>{s.total_actions}</b></div>
              <div className="text-gray-500 text-xs mt-2">
                Skipped: self-ref {s.skipped_self_reference}, dangling {s.skipped_dangling_target},
                no-mapping {s.no_mapping_for_user}
                {s.updated != null && <> · <b className="text-green-700">Updated: {s.updated}</b></>}
              </div>
            </div>
          )}

          {dryRun?.sample_actions?.length > 0 && !applied && (
            <div>
              <div className="text-sm font-semibold mb-2">Sample of upcoming changes (first 10):</div>
              <div className="text-xs font-mono bg-gray-50 border rounded p-2 max-h-48 overflow-y-auto">
                {dryRun.sample_actions.slice(0, 10).map((a, i) => (
                  <div key={i} className="truncate">
                    {a.mongo_id} · {a.agent_code || '—'} · {a.current_upline_id || 'NULL'} → <b>{a.new_upline_id}</b> ({a.match_method})
                  </div>
                ))}
              </div>
            </div>
          )}

          {dryRun && dryRun.summary.total_actions > 0 && !applied && (
            <div className="border-t pt-3 space-y-2">
              <label className="text-sm text-gray-700">
                Type <code className="bg-gray-100 px-1 rounded">APPLY</code> to confirm:
              </label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="APPLY"
                data-testid="backfill-confirm-input"
              />
              <Button
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={runApply}
                disabled={loading || confirmText !== 'APPLY'}
                data-testid="apply-backfill-btn"
              >
                {loading ? 'Applying…' : `Apply ${dryRun.summary.total_actions} changes`}
              </Button>
            </div>
          )}

          {applied && (
            <div className="bg-green-50 border border-green-300 rounded p-3 text-sm text-green-900">
              ✅ Backfill complete. {applied.summary.updated} users updated.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ==================== LEGACY SEED IMPORT DIALOG (Super Admin only) ====================
const LegacySeedImportDialog = ({ open, onOpenChange, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [dryRun, setDryRun] = useState(null);
  const [applied, setApplied] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/legacy-seed/status');
      setStatus(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setDryRun(null);
      setApplied(null);
      setConfirmText('');
      loadStatus();
    }
  }, [open]);

  const runDryRun = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/admin/legacy-seed/import?mode=dry_run');
      setDryRun(data);
      const totalActions = Object.values(data.collections || {}).reduce(
        (s, c) => s + (c.will_insert || 0) + (c.will_update || 0), 0
      );
      toast.success(`Dry run complete: ${totalActions} doc operations pending`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dry run failed');
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    if (confirmText !== 'IMPORT') {
      toast.error('Type IMPORT to confirm');
      return;
    }
    setLoading(true);
    try {
      const confirm = status?.confirm_token_for_apply;
      const PAGE_SIZE = 5000;
      const collectionNames = Object.keys(dryRun?.collections || {});
      // Seed state with dry-run numbers so UI shows all rows immediately
      const initial = { collections: {} };
      for (const name of collectionNames) {
        const dr = dryRun.collections[name];
        initial.collections[name] = {
          bundle_docs: dr.bundle_docs, existing_before: dr.existing_before,
          will_insert: dr.will_insert, will_update: dr.will_update,
          applied: 0, errors: [],
        };
      }
      setApplied(initial);

      for (const coll of collectionNames) {
        let offset = 0;
        let hasMore = true;
        let totalApplied = 0;
        const errors = [];
        while (hasMore) {
          toast.info(`Importing ${coll}${offset ? ` (offset ${offset})` : ''}…`, { duration: 1500 });
          try {
            const { data } = await api.post(
              `/admin/legacy-seed/import?mode=apply&confirm=${confirm}&collection=${coll}&offset=${offset}&limit=${PAGE_SIZE}`,
              null,
              { timeout: 180000 },
            );
            const cdata = data.collections?.[coll];
            if (!cdata) break;
            totalApplied += cdata.applied || 0;
            if (cdata.errors?.length) errors.push(...cdata.errors);
            // Functional update to guarantee React re-render each tick
            setApplied((prev) => ({
              collections: {
                ...(prev?.collections || {}),
                [coll]: {
                  ...(prev?.collections?.[coll] || {}),
                  applied: totalApplied,
                  errors: [...errors],
                },
              },
            }));
            hasMore = cdata.has_more;
            offset = cdata.next_offset;
          } catch (e) {
            const msg = e.response?.data?.detail || e.message || 'failed';
            errors.push(msg);
            setApplied((prev) => ({
              collections: {
                ...(prev?.collections || {}),
                [coll]: { ...(prev?.collections?.[coll] || {}), errors: [...errors] },
              },
            }));
            toast.error(`${coll} failed: ${msg}`);
            break;
          }
        }
      }
      toast.success('Import complete — see table for per-collection write counts');
      onComplete?.();
    } finally {
      setLoading(false);
    }
  };

  // Prefer applied (live-updating) numbers while running; fall back to dry-run preview.
  const collections = applied?.collections || dryRun?.collections || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="legacy-seed-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-rose-600" />
            Legacy Data Import (one-time production migration)
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm text-rose-900">
            Downloads the legacy data bundle from the Preview environment and upserts it into
            this DB by <code>id</code>. Scoped to <code>legacy_*</code> collections and
            <code>is_legacy=True</code> / <code>LGC-*</code> documents only — your existing
            non-legacy admins, PICs, accounts and test agents are <b>preserved</b>.
          </div>

          {status && (
            <div className="border rounded p-3 space-y-1 text-xs font-mono">
              <div className="text-sm font-semibold font-sans mb-2">Current DB state</div>
              {Object.entries(status.existing_on_this_db).map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="text-gray-600">{k}</span>
                  <span className="font-semibold">{v.toLocaleString()}</span>
                </div>
              ))}
              <div className="text-xs text-gray-500 pt-2 font-sans">
                Bundle source: <a href={status.default_bundle_url} className="text-blue-600 underline" target="_blank" rel="noreferrer">download preview bundle</a>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={runDryRun}
              disabled={loading}
              data-testid="legacy-seed-dry-run-btn"
            >
              {loading ? 'Running…' : 'Run Dry Run'}
            </Button>
          </div>

          {Object.keys(collections).length > 0 && (
            <div className="border rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Collection</th>
                    <th className="px-3 py-2 text-right">Bundle</th>
                    <th className="px-3 py-2 text-right">Existing</th>
                    <th className="px-3 py-2 text-right">Insert</th>
                    <th className="px-3 py-2 text-right">Update</th>
                    <th className="px-3 py-2 text-right">Applied</th>
                    <th className="px-3 py-2 text-left">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(collections).map(([name, c]) => (
                    <tr key={name} className="border-t">
                      <td className="px-3 py-1.5 font-mono">{name}</td>
                      <td className="px-3 py-1.5 text-right">{c.bundle_docs.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right text-gray-500">{c.existing_before.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right text-blue-700">{c.will_insert.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right text-amber-700">{c.will_update.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right text-green-700 font-semibold">{c.applied.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-left text-red-600 truncate max-w-xs">
                        {(c.errors || []).join('; ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {dryRun && !applied && (
            <div className="border-t pt-3 space-y-2">
              <label className="text-sm text-gray-700">
                Type <code className="bg-gray-100 px-1 rounded">IMPORT</code> to confirm production write:
              </label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="IMPORT"
                data-testid="legacy-seed-confirm-input"
              />
              <Button
                className="bg-rose-600 hover:bg-rose-700 text-white"
                onClick={runApply}
                disabled={loading || confirmText !== 'IMPORT'}
                data-testid="legacy-seed-apply-btn"
              >
                {loading ? 'Importing…' : 'Apply Import'}
              </Button>
            </div>
          )}

          {applied && (
            <div className="bg-green-50 border border-green-300 rounded p-3 text-sm text-green-900">
              ✅ Import complete. Review the table above for per-collection write counts.
              Next step: open "Legacy Upline Backfill" to fix the upline hierarchy.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

